import * as THREE from 'three';
import { HsbProject } from '../hsb-project';
import { GeometryHelpers } from './Helpers/GeometryHelpers';
import { HsbVisual } from '../hsb-visual';
import { HsbVisualLayer } from '../hsb-visuallayer';
import {
    BlockReference,
    CoordSys,
    Dimension,
    ElementRoofSIP,
    FastenerAssemblyEnt,
    Grid,
    HsbElement,
    HsbEntity,
    MassGroup,
    MetalPartCollectionEnt,
    NonHsb,
    ObjectType,
    Opening,
    Property,
    SceneLayerName,
    Sheet,
    Sip,
    Stack,
    TslInst,
} from '../hsbcad-model';
import { MaterialManager, MaterialPresets } from './Managers/MaterialManager';
import { ViewerLayers } from './Enums/ViewerLayers';
import { ViewerScene } from './ViewerScene';
import { LoadingProgress, LoadingSteps } from './LoadingProgress';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';
import { Viewer } from './Viewer';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';
import { SimplifyModifier } from 'three/examples/jsm/modifiers/SimplifyModifier';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { BufferGeometry, Triangle, Vector3 } from 'three';
const optimer_bold = require('three/examples/fonts/optimer_bold.typeface.json');

export class SceneLoader {
    _asyncTasks: any[] = [];
    _loadedGeometry: { [key: string]: any } = {};
    _geomObject: { [key: string]: any } = {};
    _currentScene!: ViewerScene | null;
    _currentVisualLayer!: HsbVisualLayer | null;
    _optimerBold!: Font;

    useInstancedMeshes: boolean = false;

    private _renderPerObjectsPercentage: number = 5;
    public get renderPerObjects(): number {
        return this._renderPerObjectsPercentage;
    }
    public set renderPerObjects(v: number) {
        this._renderPerObjectsPercentage = Math.min(100, Math.max(0, v));
    }

    constructor(
        private _viewer: Viewer,
        private materialManager: MaterialManager,
        private loadingProgress: LoadingProgress
    ) {
        let loader = new FontLoader();
        this._optimerBold = loader.parse(optimer_bold);
    }

    clear() {
        this._asyncTasks.forEach((timer) => {
            clearTimeout(timer);
        });
        this._asyncTasks = [];
    }

    /**
     * Creates a visual layer for a project
     * @param project The project for which to create a visual layer
     */
    CreateVisualLayer(project: any, scene: ViewerScene) {
        let self = this;
        self._currentVisualLayer = new HsbVisualLayer(this.materialManager);
        self._currentScene = scene;
        let numModels = Object.keys(project.HsbModels).length;
        this._loadedGeometry = {};
        let count = 0;
        var promises: Promise<any>[] = [];
        return new Promise<any>(function (resolve, reject) {
            let buffers = Object.values(project.HsbModels).map((m: any) => m.geomBuffers);
            for (var modelkey in project.HsbModels) {
                count++;
                let hsbModel = project.HsbModels[modelkey];

                if (!hsbModel) reject('Viewer::CreateVisualLayer: There was no model present!"');

                if (hsbModel.hfdm) {
                    // eslint-disable-next-line no-loop-func
                    promises.push(
                        self
                            .CreateGeometry(modelkey, project)
                            .then((root) => {
                                if (count === numModels) {
                                    resolve(root);
                                }
                            })
                            .catch((error) => {
                                console.error(
                                    'Viewer::CreateVisualLayer.CreateGeometry():' + error
                                );
                                reject(error);
                            })
                    );
                } else if (hsbModel.geomBuffers) {
                    self.loadingProgress.status = 'Test';
                    hsbModel.geomBuffers.forEach((geomBuffer: THREE.BufferGeometry) => {
                        promises.push(self.CreateGeometry(geomBuffer, project));
                    });
                }
            }
            Promise.all(promises)
                .then((roots) => {
                    project.visualLayer = self._currentVisualLayer;
                    if (!project.visualLayer) reject('No visual layer created');
                    self._viewer.requestRenderFrame();
                    self.createHSBEntityLabels(self._currentScene!, project);
                    resolve({
                        visualLayer: self._currentVisualLayer,
                        roots: roots,
                    });

                    let clearTask = setTimeout(() => {
                        self._currentVisualLayer = null;
                        self._currentScene = null;
                        self._loadedGeometry = {};
                    });
                    self._asyncTasks.push(clearTask);
                })
                .catch((error) => {
                    console.error('Viewer::CreateVisualLayer().AllPromises():' + error);
                    reject(error);
                });
        });
    }

    /**
     * Creates THREE objects for geometry
     * @param geometry The geometry data that should be loaded
     * @param project The projec thtat was loaded
     */
    CreateGeometry(geometry: any, project: any): Promise<any> {
        let self = this;
        self.loadingProgress.status = 'Creating geometry';

        // Check if geom was already created
        if (self._loadedGeometry[geometry.filepath]) {
            // Added the geom buffer step per model
            self.loadingProgress.value +=
                LoadingSteps.geomBuffer / Object.keys(project.HsbModels).length;
            return Promise.reject('Already created');
        }
        var entitieslayer = self._currentScene?.GetLayer(SceneLayerName.Entities);

        // Creating models
        return self.CreateModels(project, self._currentVisualLayer!, entitieslayer, geometry);
    }

    /**
     * Creates Objects in the THREE framework for the given entities.
     * @param project The project that was loaded
     * @param visualLayer THe visual layer upon which to connect the visuals
     * @param entitieslayer THe entities layer upon which to connect the entities
     * @param geomBuffer The buffer containing all geometry
     */
    public CreateModels(
        project: any,
        visualLayer: HsbVisualLayer,
        entitieslayer: any,
        geomBuffer: any
    ): Promise<any> {
        let self = this;
        let root = new THREE.Group();
        root.rotateX(-Math.PI / 2);
        root.name = 'Root: ' + geomBuffer.modelId;
        entitieslayer.add(root);
        let entitygroup = project.allHsbEntities(false);
        return new Promise(function (resolve, reject) {
            try {
                if (!self.materialManager) {
                    console.error('Sceneloader::CreateModels(): No material manager!');
                }

                var model: any = null;
                let needsTranslation = false;

                if (!geomBuffer.modelId) {
                    let buff = String(geomBuffer);
                    model = project.HsbModels[buff];
                    needsTranslation = true;
                } else {
                    model = project.HsbModels[geomBuffer.modelId];
                }

                // Out if no model was present
                if (!model) reject('no model present');
                let entities: HsbEntity[] = entitygroup ?? model!.Entities;
                entities = entities.filter((e) => e.hsbmodel.modelId === geomBuffer.modelId);
                let entityCreationPromies = self.interpretEntities(
                    entities,
                    visualLayer,
                    entitieslayer,
                    geomBuffer,
                    root,
                    needsTranslation,
                    project
                );
                Promise.all(entityCreationPromies)
                    .then((res) => {
                        resolve(root);
                    })
                    .catch((err) => {
                        console.error(err);
                        reject(err);
                    });
            } catch (error) {
                console.error('Viewer::CreateModels():' + error);
                reject(error);
            }
        });
    }
    /**
     * This method checks wether the given geometry buffer has grouped geometries based on posnums
     * @param posNum The posbnum for these entities
     * @param geomBuffer The geometry buffer for all entities
     * @param indexedEntities The entities that were grouped
     * @param logDifferences Should the differences be logged if presen
     * @returns True if entities with the same posnum have the same geometry provided, false otherwise.
     */
    checkPosnumBasedGeom(
        posNum: string,
        geomBuffer: { data: { [x: string]: any } },
        indexedEntities: any[],
        logDifferences = false
    ) {
        try {
            // Get geometries
            let bufferGeoms = indexedEntities.map((e: { geomhandle: any; handle: any }) => {
                let handle = e.geomhandle ?? e.handle;
                return geomBuffer.data[handle];
            });
            if (bufferGeoms.some((bg: any) => !bg)) {
                return false;
            }
            if (bufferGeoms.length <= 1) {
                return false;
            }

            // CHoose geometry to test against
            let testGeometry = bufferGeoms[0];
            let indices: any[] = [];
            // CHeck for differences
            let differingGeometries = bufferGeoms.filter((bg: { buffer: any[] }, index: any) => {
                let test = bg.buffer.some((element: number, index: string | number) => {
                    return Math.abs(element - testGeometry.buffer[index]) > 0.1;
                });
                if (test) {
                    indices.push(index);
                }
                return test;
            });
            let hasDifferences = differingGeometries.length > 0;
            // Logging
            if (logDifferences && hasDifferences) {
                differingGeometries = differingGeometries.map((bg: { buffer: any[] }) => {
                    let diff: any[] = [];
                    bg.buffer.forEach((element: number, index: string | number) => {
                        if (Math.abs(element - testGeometry.buffer[index]) > 0.1) {
                            diff.push({
                                index: index,
                                orig: testGeometry.buffer[index],
                                diff: element,
                            });
                        }
                    });
                    return { buffer: bg.buffer, diffs: diff };
                });
                console.warn(posNum, differingGeometries, bufferGeoms);
            }
            return !hasDifferences;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    interpretEntities(
        entities: any[],
        visualLayer: HsbVisualLayer,
        entitieslayer: any,
        geomBuffer: { modelId: string | number; data: { [x: string]: any } },
        root: THREE.Group,
        needsTranslation: boolean,
        project: HsbProject
    ) {
        let self = this;
        // Increase to reflect the loading progress per entity
        let entityIncrease =
            LoadingSteps.geomBuffer /
            entities.length /
            (project.HsbModels[geomBuffer.modelId]?.geomBuffersCount ?? 1) /
            Object.keys(project.HsbModels).length;
        if (!this.useInstancedMeshes) {
            this.loadingProgress.value +=
                LoadingSteps.wireFrameCreation /
                (project.HsbModels[geomBuffer.modelId]?.geomBuffersCount ?? 1) /
                Object.keys(project.HsbModels).length;
            return entities.map((entity: any, index: any) =>
                this.createIndividualEntity(
                    entity,
                    entityIncrease,
                    geomBuffer,
                    root,
                    visualLayer,
                    needsTranslation,
                    project,
                    entitieslayer,
                    index
                )
            );
        } else {
            // Checks whether entities have common geometry
            let handleTest: { [key: string]: any[] } = entities.reduce(
                (
                    accu: { [x: string]: any[] },
                    entity: {
                        _props: { posnum: { _value: string } };
                        geomhandle: string;
                    }
                ) => {
                    let posnum = entity._props?.posnum?._value ?? 'none';
                    let key = 'individual';
                    if (posnum !== 'none' && posnum != '-1') {
                        // valid posnum
                        key = posnum;
                    } else if (entity.geomhandle) {
                        // valid geomhandle
                        key = entity.geomhandle;
                    }

                    if (accu && accu[key]) {
                        accu[key].push(entity);
                    } else {
                        accu[key] = [entity];
                    }
                    return accu;
                },
                {}
            );

            // Creating the actual entities

            let allTasks: Promise<any>[] = [];
            Object.entries(handleTest).forEach(([key, indexedEntities]) => {
                // If individual entities (can't be isntanced) or single entities (no advantage to instancing them)
                if (key === 'individual' || indexedEntities.length === 1) {
                    allTasks.push(
                        ...indexedEntities.map((entity, index) => {
                            return this.createIndividualEntity(
                                entity,
                                entityIncrease,
                                geomBuffer,
                                root,
                                visualLayer,
                                needsTranslation,
                                project,
                                entitieslayer,
                                index
                            );
                        })
                    );
                }
                // Else we can use instanced meshes for these
                else if (indexedEntities[0].geomhandle) {
                    let bufferGeom = geomBuffer.data[indexedEntities[0].geomhandle];
                    if (bufferGeom) {
                        allTasks.push(
                            this.createDuplicateEntity(
                                indexedEntities,
                                bufferGeom,
                                entityIncrease,
                                root
                            )
                        );
                    }
                }
                // Check if we the actual provided posbum geom is the same
                /**
                 * @todo Check why this is breaking
                 */
                else if (this.checkPosnumBasedGeom(key, geomBuffer, indexedEntities)) {
                    let bufferGeom = geomBuffer.data[indexedEntities[0].handle];
                    if (bufferGeom) {
                        allTasks.push(
                            this.createDuplicateEntity(
                                indexedEntities,
                                bufferGeom,
                                entityIncrease,
                                root
                            )
                        );
                    }
                } else {
                    // Else we need to create individuals
                    allTasks.push(
                        ...indexedEntities.map((entity, index) =>
                            this.createIndividualEntity(
                                entity,
                                entityIncrease,
                                geomBuffer,
                                root,
                                visualLayer,
                                needsTranslation,
                                project,
                                entitieslayer,
                                index
                            )
                        )
                    );
                }
            });
            // Aggregate all entity tasks (necessary if we want to stop them during their runtime)

            /**
             * @todo At this point merged wireframe is not necessary due to instanced meshes.
             */
            self.loadingProgress.status = 'Creating wireframes';
            // let mergeTask = setTimeout(() => {
            //     let merge = self.createMergedGeometry(root.children, root);
            //     root.add(merge);
            //      let wireframe = self.createMergedWireframe(root.children, root);
            //      root.add(wireframe);
            //     this.loadingProgress.value += LoadingSteps.wireFrameCreation / (project.HsbModels[geomBuffer.modelId]?.geomBuffersCount ?? 1);
            //     self._viewer.currentScene.WireFrame = wireframe;
            // });
            // self._asyncTasks.push(mergeTask);

            this.loadingProgress.value += LoadingSteps.wireFrameCreation;
            return allTasks;
        }
    }
    /**
     * Creates an object for the given entity and returns this operation.
     * @param entity The entity for which to create an object
     * @param entityIncrease the increase to the loading progress per entity
     * @param renderTreshold For this amount of entities created there should eb a render triggered
     * @param geomBuffer the geometry buffer
     * @param root The root to which the objects should eb attached
     * @param visualLayer
     * @param needsTranslation Whether the entity still needs a translation
     * @param project The project these entities belong to
     * @param entitieslayer The root of the entity objects
     * @param index The current index of the given entity
     * @returns The async task of creating the object
     */
    private createIndividualEntity(
        entity: any,
        entityIncrease: number,
        geomBuffer: { modelId: any; data: { [x: string]: any } },
        root: THREE.Object3D,
        visualLayer: HsbVisualLayer,
        needsTranslation: any,
        project: HsbProject,
        entitieslayer: any,
        index: number
    ) {
        let self = this;
        return new Promise((resolve, reject) => {
            self.loadingProgress.value += entityIncrease;
            //Check if ID's match
            if (entity.hsbmodel.modelId !== geomBuffer.modelId)
                return reject('HsbModel and Geombuffer not aligned!');
            //Check if empty tsl inst
            if (entity instanceof TslInst && entity.groups.length === 0)
                return resolve('Empty group TSL instance');
            if (entity instanceof BlockReference) {
                resolve('Block ref');
                return;
            }
            //Check if stack
            if (entity instanceof Stack) resolve('Stack');
            if (entity instanceof Grid) {
                let gridObject = new THREE.GridHelper(
                    100000,
                    entity.gridlineXIntersections.length ?? 10
                );
                gridObject.name = 'grid';
                // if (entity.gridlineXIntersections) {
                //     self.addGridLines(entity, gridObject, entity.gridlineXIntersections, true);
                // }
                // if (entity.gridlineYIntersections) {
                //     self.addGridLines(entity, gridObject, entity.gridlineYIntersections, false);
                // }
                gridObject.userData.handle = entity.handle;
                entity.object = gridObject;
                // self.ApplyMaterial(entity);
                return resolve(gridObject);
            }
            if (entity instanceof Dimension) {
                var textsize = entity.hsbmodel.Units.convert(
                    entity.Textheight ? entity.Textheight : 50
                );
                //LineGeom
                var origin = entity.coordSys.ptOrg.clone();
                var dir = entity.XDirection;
                var ray = new THREE.Ray(origin, dir);
                const points: THREE.Vector3[] = [];
                if (!entity.Nodes || entity.Nodes.length < 2)
                    return reject('No nodes for Dimension TSL');
                entity.Nodes.forEach((node: { pt: THREE.Vector3 }) => {
                    var closest = new THREE.Vector3();
                    ray.closestPointToPoint(node.pt, closest);
                    points.push(closest);
                });
                const geom = new THREE.BufferGeometry().setFromPoints(points);
                let dimObject = new THREE.Line(geom, self.materialManager.getLinematerial());
                dimObject.name = 'Dimension';
                if (entity.ShowArrows) {
                    dimObject.add(self.CreateArrow(points[1], points[0], 30));
                    dimObject.add(
                        self.CreateArrow(points[points.length - 2], points[points.length - 1], 30)
                    );
                }
                if (entity.ShowSegments) {
                    for (let i = 0; i < points.length - 1; ++i) {
                        let startPT = points[i];
                        let endPT = points[i + 1];
                        let midPT = new THREE.Vector3(
                            (startPT.x + endPT.x) / 2,
                            (startPT.y + endPT.y) / 2,
                            (startPT.z + endPT.z) / 2
                        );
                        midPT.add(entity.YDirection.clone().multiplyScalar(50));
                        //Get formatted distance string
                        /**
                         * @todo: get unitconversion in here
                         */
                        //var distance = _self.unitConversionService.Convert(startPT.distanceTo(endPT), UnitTypes.LENGTH);
                        var distance = startPT.distanceTo(endPT);
                        //Create label
                        let label = self.createTextSpriteObject(distance.toString(), {
                            fontsize: 50,
                        });
                        if (label) {
                            label.onBeforeRender = function (
                                renderer,
                                scene,
                                camera,
                                geometry,
                                material,
                                group
                            ) {
                                let dist = label!.position.distanceTo(camera.position);
                                label!.scale.set(
                                    Math.max(
                                        renderer.domElement.width,
                                        (dist * renderer.domElement.width) / 800
                                    ),
                                    Math.max(
                                        renderer.domElement.height,
                                        (dist * renderer.domElement.height) / 800
                                    ),
                                    1
                                );
                                label!.updateMatrix();
                            };
                            label.name = 'Label';
                            label.position.copy(midPT);
                            dimObject.add(label);
                        }
                    }
                    //Segments dividers
                    if (points.length > 2) {
                        for (let i = 1; i < points.length - 1; ++i) {
                            //Create offsets
                            let segPoint: THREE.Vector3[] = [];
                            let startPoint = points[i].clone();
                            let endPoint = points[i].clone();
                            let vecZ = new THREE.Vector3().crossVectors(
                                entity.XDirection,
                                entity.YDirection
                            );
                            let vecX = entity.XDirection.clone();
                            vecZ.multiplyScalar(textsize);
                            vecX.multiplyScalar(textsize);
                            segPoint.push(startPoint.add(vecZ).add(vecX));
                            segPoint.push(endPoint.sub(vecZ).sub(vecX));
                            //Create line
                            let segGeom = new THREE.BufferGeometry().setFromPoints(segPoint);
                            let segObject = new THREE.Line(
                                segGeom,
                                self.materialManager.getLinematerial()
                            );
                            segObject.name = 'Divider';
                            dimObject.add(segObject);
                        }
                    }
                }
                // References
                if (entity.ShowReferences) {
                    dimObject.add(self.CreateRefLine(points[0], entity.Nodes[0]));
                    dimObject.add(
                        self.CreateRefLine(
                            points[points.length - 1],
                            entity.Nodes[entity.Nodes.length - 1]
                        )
                    );
                }
                dimObject.userData.handle = entity.handle;
                entity.object = dimObject;
                // Hides object if further than 50000 units
                // TODO: onBeforeRender doesnt trigger if object.visible === false;
                // Find better solution
                dimObject.onBeforeRender = function (
                    renderer,
                    scene,
                    camera,
                    geometry,
                    material,
                    group
                ) {
                    if (!camera) return;
                    let dist = dimObject.position.distanceTo(camera.position);
                    dimObject.userData.visual.show(dist < 30000);
                    dimObject.children.forEach((child) => {
                        child.visible = dist < 30000;
                    });
                };

                self.ApplyMaterial(entity);
                resolve(dimObject);
                return;
            }
            if (entity instanceof HsbElement) {
                // Create a body if we don't have any child entities
                if (entity.entities && entity.entities.length > 0) {
                    entity.object = new THREE.Object3D();
                    self.ApplyMaterial(entity, self.materialManager.getGhostmaterial());
                    resolve(entity.object);
                    return;
                }
            }
            if (entity) {
                let bufferGeom: any = null;
                let processedObject = false;
                let coordsys = entity.coordSys.clone();
                let handle = entity.geomhandle ?? entity.handle;
                if (entity.initialCoordSys) {
                    coordsys = entity.initialCoordSys.clone();
                }
                if (needsTranslation || entity.needsTranslation) {
                    bufferGeom = self.CreateBufferGeometry(entity);
                } else {
                    bufferGeom = geomBuffer.data[handle];
                    if (visualLayer.visuals[entity.hsbmodel.modelId as string]) {
                        processedObject =
                            visualLayer.visuals[entity.hsbmodel.modelId][entity.handle];
                    }
                }
                if (bufferGeom && !processedObject) {
                    // Special case...
                    if (entity.objecttype === ObjectType.IfcSpace) {
                        if (
                            !(
                                entity.searchnode &&
                                entity.searchnode.objecttype === entity.objecttype_
                            )
                        ) {
                            self._geomObject[handle] = true;
                            resolve('Special IFC case');
                            return;
                        }
                    }
                    // Create a mesh from the geombuffer present in the data
                    var body = self.CreateObjectFromBuffer(
                        bufferGeom,
                        handle,
                        entity.hsbmodel.Units.factor,
                        self.materialManager.getBeamMaterial()
                    );
                    if (body) {
                        body.name = entity.handle;
                        // Setting proper transform
                        var m4ModelToTable = new THREE.Matrix4();
                        if (!(entity instanceof HsbElement)) {
                            m4ModelToTable.makeBasis(coordsys.vecX, coordsys.vecY, coordsys.vecZ);
                            m4ModelToTable.setPosition(coordsys.ptOrg);
                            body.applyMatrix4(m4ModelToTable);
                        }
                        body.updateMatrix();
                        body.matrixAutoUpdate = false;
                        // Setting up bounding boxes
                        body.geometry.computeBoundingBox();
                        entity.boundingBox = body?.geometry?.boundingBox?.clone();
                        // Formatting size
                        let box = new THREE.Box3().setFromObject(body);
                        let size = box.getSize(new THREE.Vector3());
                        let format = (val) =>
                            this._viewer.formatting.format(val, {
                                from: 'mm',
                                to: entity.hsbmodel.Units.unit,
                                valueOnly: true,
                            });
                        let formatVector = (val: THREE.Vector3) =>
                            new THREE.Vector3(format(val.x), format(val.y), format(val.z));
                        
                    
                        size = formatVector(size);
                        let position = body.getWorldPosition(new THREE.Vector3());
                        position = formatVector(position);
                        // Adding the properties
                        entity.addProperty(
                            new Property('Position X', 'Geometry', position.x, true)
                        );
                        entity.addProperty(
                            new Property('Position Y', 'Geometry', position.y, true)
                        );
                        entity.addProperty(
                            new Property('Position Z', 'Geometry', position.z, true)
                        );
                        // Set properties
                        entity.addProperty(new Property('Size X', 'Geometry', size.x, true));

                        entity.addProperty(new Property('Size Y', 'Geometry', size.y, true));
                        entity.addProperty(new Property('Size Z', 'Geometry', size.z, true));
                        entity.addProperty(
                            new Property(
                                'Volume',
                                'Geometry',
                                size.x * size.y * size.z,
                                true,
                                'Property.Volume'
                            )
                        );

                        // Userdata
                        body.userData.color = entity.color;
                        body.userData.modelId = geomBuffer.modelId;
                        body.userData.handle = entity.handle;
                        body.userData.visible = true;
                        entity.object = body;
                        body.userData.transparent = false;
                        self.ApplyMaterial(entity);
                        self._geomObject[handle] = true;
                        root.add(body);
                        resolve(body);
                    } else {
                        reject('Could not create body');
                    }
                } else resolve('No buffer geometry');
            } else resolve('No Entity');
        });
    }

    /**
     * Creates an instanced mesh for the given entities.Ensure that all entities have the same geometry or there will be wrong meshes.
     * @param entities The entities for which to create the instanced mesh
     * @param geomBuffer The geometry buffer
     * @param entityIncrease The increase to the loading progress per entitity
     * @param root The root to which the object should be attached
     * @returns The async task of creating the instanced meshe
     */
    private createDuplicateEntity(
        entities: string | any[],
        geomBuffer: any,
        entityIncrease: number,
        root: THREE.Object3D
    ) {
        let self = this;
        return new Promise((resolve, reject) => {
            let res = this.CreateInstanceObjectsFromBuffer(
                geomBuffer,
                entities.length,
                this.materialManager.getBeamMaterial()
            );
            if (!res) {
                return reject('Could not create instance');
            }
            var { body, bufferGeometry } = res;

            // Wireframe group
            let wireFrameGroup = new THREE.Group();
            wireFrameGroup.name = 'wireframes';
            wireFrameGroup.layers.set(ViewerLayers.WIREFRAME);
            body.add(wireFrameGroup);
            // Init
            body.userData.handle = [];
            body.userData.color = [];
            body.userData.state = [];
            body.userData.visible = Array(entities.length).fill(true);
            body.layers.set(ViewerLayers.MODELS);
            body.userData.modelId = entities[0].hsbmodel.modelId;

            let lineGeom = new THREE.EdgesGeometry(bufferGeometry);
            for (let index = 0; index < entities.length; index++) {
                // Transform
                const entity = entities[index];
                let coordSys =
                    entity.initialCoordSys?.clone() ?? entity.coordSys?.clone() ?? new CoordSys();
                var entityM4 = new THREE.Matrix4();
                if (!(entity instanceof HsbElement)) {
                    entityM4.makeBasis(coordSys.vecX, coordSys.vecY, coordSys.vecZ);
                    entityM4.setPosition(coordSys.ptOrg);
                    body.setMatrixAt(index, entityM4);
                }
                body.setColorAt(index, new THREE.Color(entity.color));
                //Userdata
                body.userData.handle[index] = entity.handle;
                body.userData.color[index] = entity.color;
                entity.object = body;
                entity.instanceId = index;
                // Visual creation
                let wireframe = new THREE.LineSegments(
                    lineGeom,
                    this.materialManager.getLineBasicmaterial()
                );
                wireframe.applyMatrix4(entityM4);
                wireframe.layers.set(ViewerLayers.WIREFRAME);
                wireframe.name = 'wireframe:' + entity.handle;
                wireFrameGroup.attach(wireframe);
            }
            // Updating loading
            self.loadingProgress.value += entityIncrease * entities.length;
            body.updateMatrix();
            // Scene
            root.add(body);
            resolve(body);
        });
    }

    public createMergedGeometry(objects: THREE.Object3D[], root: THREE.Object3D) {
        const modifier = new SimplifyModifier();
        let simplifyFactor = 0.5;
        let simplifyTreshold = 100;
        let filteredObj = objects.filter(
            (o) => o instanceof THREE.Mesh && o.geometry instanceof THREE.BufferGeometry
        ) as THREE.Mesh[];
        if (filteredObj.length === 0) return null;
        let bufferGeoms = filteredObj.map((o: THREE.Mesh) => {
            let geom = o.geometry.clone() as THREE.BufferGeometry;
            if (geom.attributes.position.count < simplifyTreshold)
                return geom.applyMatrix4(o.matrix);
            let count = Math.floor(geom.attributes.position.count * (1 - simplifyFactor));
            try {
                geom.computeVertexNormals();
                let simpleGeom = modifier.modify(geom, count);
                // Something went wrong
                if (simpleGeom.attributes.position.count === 0) {
                    return geom.applyMatrix4(o.matrix);
                } else {
                    simpleGeom.computeVertexNormals();
                    return simpleGeom.applyMatrix4(o.matrix);
                }
            } catch (err: any) {
                return geom.applyMatrix4(o.matrix);
            }
        });
        let materials = filteredObj.map((o: THREE.Mesh) => {
            let mat = (o.material as THREE.MeshStandardMaterial).clone();
            mat.flatShading = true;
            return mat;
        });

        let singleGeometry = (BufferGeometryUtils as any).mergeBufferGeometries(bufferGeoms, true);
        let mesh = new THREE.Mesh(singleGeometry, materials);
        mesh.name = 'Simplified Model';
        mesh.layers.set(ViewerLayers.SIMPLEMODEL);
        return mesh;
    }

    public createMergedWireframe(objects: THREE.Object3D[], root: THREE.Object3D) {
        let filteredObj = objects.filter(
            (o) => o instanceof THREE.Mesh && o.geometry instanceof THREE.BufferGeometry
        ) as THREE.Mesh[];
        let bufferGeoms = filteredObj.map((o: THREE.Mesh) => {
            let geom = o.geometry.clone() as THREE.BufferGeometry;
            geom.applyMatrix4(o.matrix);
            return geom;
        });
        const mergedGeom = (BufferGeometryUtils as any).mergeBufferGeometries(bufferGeoms, true);

        const edgesGeom = new THREE.EdgesGeometry(mergedGeom);
        const geo = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeom);
        const wireframe = new THREE.LineSegments(
            edgesGeom,
            this.materialManager.getLineBasicmaterial()
        );
        wireframe.computeLineDistances();
        wireframe.layers.set(ViewerLayers.SIMPLEMODEL);
        // wireframe.userData.originalmaterial = this.materialManager.getLineBasicmaterial();
        return wireframe;
    }

    /**
     * Sets the material depending on the type of entity. If no material is given it will apply the default material.
     * @param visual The visual to which the material should be applied
     * @param material (Optional) Material to override the default material for this type
     */
    ApplyMaterial(entity: HsbEntity, material?: THREE.Material) {
        if (!this.materialManager) {
            console.error("Sceneloader::ApplyMaterial(): No materialmanager on 'this'!", this);
            return;
        }
        let objectmaterial = material;
        if (!objectmaterial) {
            if (entity instanceof Sheet) {
                objectmaterial = this.materialManager.getPresetMaterial(MaterialPresets.SHEET, {
                    color: entity.color,
                    opacity: entity.transparency,
                });
            } else if (entity instanceof NonHsb) {
                objectmaterial = this.materialManager.getNonHsbMaterial();
            } else if (entity instanceof HsbElement) {
                objectmaterial = this.materialManager.getNonHsbMaterial();
            } else if (
                entity instanceof MassGroup ||
                entity instanceof TslInst ||
                entity instanceof FastenerAssemblyEnt ||
                entity instanceof MetalPartCollectionEnt
            ) {
                objectmaterial = this.materialManager.getPresetMaterial(MaterialPresets.METAL, {
                    color: entity.color,
                    opacity: entity.transparency,
                });
            } else if (entity instanceof Opening) {
                objectmaterial = this.materialManager.getPresetMaterial(MaterialPresets.WINDOW, {
                    color: entity.color,
                    opacity: entity.transparency,
                });
            } else if (entity instanceof Grid || entity instanceof Dimension) {
                objectmaterial = this.materialManager.getPresetMaterial<THREE.LineBasicMaterial>(
                    MaterialPresets.LINE
                );
            } else {
                objectmaterial = this.materialManager.getPresetMaterial(MaterialPresets.BEAM, {
                    color: (entity as any).color,
                    opacity: (entity as any).transparency,
                });
            }
        }

        if (entity.object instanceof THREE.Mesh) {
            entity.object.material = objectmaterial;
            entity.object.userData.originalmaterial = objectmaterial;
            entity.object.userData.materialHsbId = objectmaterial.userData.materialHsbId;
        }
    }

    /**
     * Creates buffer geometry from an entity's data.
     * @param entity THe entity from which to extract the geometry data.
     */
    public CreateBufferGeometry(entity: {
        bodydata: { faces: any[]; vertexpts: THREE.Vector3[]; coordSys: any };
        coordSys: any;
    }) {
        //Early out if no body data
        if (!entity.bodydata) return;

        var geometry = new BufferGeometry();
        var faces: Triangle[] = [];

        entity.bodydata.faces.forEach(function (face) {
            var facearray = face.slice(0);
            var facepts = facearray.map(function (index: number) {
                return entity.bodydata.vertexpts[index];
            });
            var mainfacenormal = GeometryHelpers.NotNormalizedNormalFromVertices(facepts);

            var coordSys = new CoordSys();
            coordSys.ptOrg = facepts[0];
            coordSys.vecX = facepts[1].clone().sub(facepts[0]).normalize();
            coordSys.vecZ = mainfacenormal;
            var vec = new THREE.Vector3();
            vec.crossVectors(coordSys.vecX, coordSys.vecZ);
            coordSys.vecY = vec;

            var localcoordSys = new CoordSys();

            var matrixtrans = new THREE.Matrix4();
            GeometryHelpers.setToAlignCoordSys(
                matrixtrans,
                coordSys.ptOrg,
                coordSys.vecX,
                coordSys.vecY,
                coordSys.vecZ,
                localcoordSys.ptOrg,
                localcoordSys.vecX,
                localcoordSys.vecY,
                localcoordSys.vecZ
            );

            var faceptstrans = facepts.map(function (pt: THREE.Vector3) {
                return pt.clone().applyMatrix4(matrixtrans);
            });
            var triangles = THREE.ShapeUtils.triangulateShape(faceptstrans, []);

            for (var i = 0; i < triangles.length; i++) {
                let a, b, c;
                a = entity.bodydata.vertexpts[facearray[triangles[i][0]]];
                b = entity.bodydata.vertexpts[facearray[triangles[i][1]]];
                c = entity.bodydata.vertexpts[facearray[triangles[i][2]]];

                var triangle = new Triangle(a, b, c);
                // Check that the face created from the triangulateShape has the same normal (point direction) - if not reverse the points.
                if (triangle.isFrontFacing(mainfacenormal)) {
                    triangle = new Triangle(c, b, a);
                }
                faces.push(triangle);
            }
        });

        // Create a buffer for the bufferedgeometry - this type is much faster for rendering
        var bufferpoints: Float32Array = faces.reduce((accu: Float32Array, face, index) => {
            let triIndex = index * 9;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            accu[triIndex++] = face.a.x;
            return accu;
        }, new Float32Array(faces.length * 9));
        return bufferpoints;
    }

    /**
     * Returns a mesh created from teh buffer geometry provided.
     * @param bufferGeom Buffergeometry for which to create a mesh
     * @param material THREE.Material to use for the mesh.
     */
    public CreateObjectFromBuffer(
        bufferGeom: any,
        handle: string,
        unitFactor: number,
        material: THREE.Material
    ) {
        // Early out
        if (!bufferGeom) {
            console.warn(
                'Sceneloader::CreateObjectFromBuffer(): No buffer geometry was provided. Returning null'
            );
            return;
        }
        let buffer = bufferGeom.buffer ? bufferGeom.buffer : bufferGeom;

        var bufferGeometry = new THREE.BufferGeometry();
        bufferGeometry.setAttribute(
            'position',
            new THREE.BufferAttribute(new Float32Array(buffer), 3)
        );
        bufferGeometry.computeVertexNormals();
        /**
         * @todo merge double vertices and recalculate indices. Currently jsut emrging will change how the indices are set.
         */
        // bufferGeometry = BufferGeometryUtils.mergeVertices(bufferGeometry);

        var body = new THREE.Mesh(bufferGeometry, material);
        body.layers.set(ViewerLayers.MODELS);
        let edgeGeometry = new THREE.EdgesGeometry(bufferGeometry, 15);
        const geo = new LineSegmentsGeometry().fromEdgesGeometry(edgeGeometry);
        const wireframe = new THREE.LineSegments(
            edgeGeometry,
            this.materialManager.getLineBasicmaterial()
        );
        wireframe.computeLineDistances();
        wireframe.scale.set(1, 1, 1);
        wireframe.layers.set(ViewerLayers.WIREFRAME);
        wireframe.name = 'wireframe:' + handle;
        // wireframe.userData.originalmaterial = this.materialManager.getLineBasicmaterial();
        body.add(wireframe);
        return body;
    }

    public CreateInstanceObjectsFromBuffer(
        bufferGeom: { buffer: any },
        count: number,
        material: any
    ) {
        // Early out
        if (!bufferGeom) {
            console.warn(
                'Sceneloader::CreateObjectFromBuffer(): No buffer geometry was provided. Returning null'
            );
            return;
        }
        let buffer = bufferGeom.buffer ?? bufferGeom;

        var bufferGeometry = new THREE.InstancedBufferGeometry();
        bufferGeometry.setAttribute(
            'position',
            new THREE.BufferAttribute(new Float32Array(buffer), 3)
        );
        bufferGeometry.computeVertexNormals();
        var body = new THREE.InstancedMesh(bufferGeometry, material.clone(), count);
        return { body, bufferGeometry };
    }

    computeWireframeLineDistances(geometry: {
        isBufferGeometry: any;
        index: null;
        attributes: { position: any };
        setAttribute: (arg0: string, arg1: THREE.Float32BufferAttribute) => void;
        isGeometry: any;
    }) {
        const _start = /*@__PURE__*/ new THREE.Vector3();

        const _end = /*@__PURE__*/ new THREE.Vector3();

        if (geometry.isBufferGeometry) {
            // we assume non-indexed geometry
            if (geometry.index === null) {
                const positionAttribute = geometry.attributes.position;
                const lineDistances: any[] = [];

                for (let i = 0, l = positionAttribute.count; i < l; i += 2) {
                    _start.fromBufferAttribute(positionAttribute, i);

                    _end.fromBufferAttribute(positionAttribute, i + 1);

                    lineDistances[i] = i === 0 ? 0 : lineDistances[i - 1];
                    lineDistances[i + 1] = lineDistances[i] + _start.distanceTo(_end);
                }

                geometry.setAttribute(
                    'lineDistance',
                    new THREE.Float32BufferAttribute(lineDistances, 1)
                );
            } else {
                console.warn(
                    'THREE.LineSegments.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.'
                );
            }
        } else if (geometry.isGeometry) {
            console.error(
                'THREE.LineSegments.computeLineDistances() no longer supports THREE.Geometry. Use THREE.BufferGeometry instead.'
            );
        }

        return this;
    }

    // --- HELPERS ----

    /**
     *
     * @param entitieslayer
     * @param visual
     * @todo refactor
     */
    AddSpaceLabel(
        project: HsbProject,
        entitieslayer: any,
        visual: HsbVisual,
        visualLayer: HsbVisualLayer
    ) {
        let entity = visual.entity;
        var unitFactor = entity.hsbmodel.Units.factor;
        if (
            entity.objecttype === ObjectType.IfcSpace &&
            entity.searchnode &&
            entity.searchnode.objecttype === entity.objecttype
        ) {
            if (entity.object) {
                var resolvedfaces = entity.geometryModel.getResolvedFaces();

                entity.object.geometry.computeBoundingBox();
                var aabb = new THREE.Box3().setFromObject(entity.object);
                var center = aabb.getCenter(new THREE.Vector3());

                // Find the lowest face.
                var vecX = new THREE.Vector3();
                var vecY = new THREE.Vector3();
                var vecZ = new THREE.Vector3();
                entity.geometryModel.matrixWorld.extractBasis(vecX, vecY, vecZ);
                var foundalignedfaces = resolvedfaces;

                if (foundalignedfaces.length > 0) {
                    foundalignedfaces.sort(function (a: any, b: any) {
                        return a.geom.vertices[0].z - b.geom.vertices[0].z;
                    });

                    foundalignedfaces[0].geom.computeBoundingBox();

                    let topface = foundalignedfaces[0];

                    let labelvisual = new HsbVisual();
                    labelvisual.entity = entity;
                    labelvisual.selectmaterial = this.materialManager.getSelectMaterial();
                    labelvisual.highlightmaterial = this.materialManager.getHighlightMaterial();
                    labelvisual.hidematerial = this.materialManager.getGhostmaterial();
                    visualLayer.addVisual(entity.hsbmodel.modelId, labelvisual);

                    var material = this.materialManager.getGreenmaterial();
                    topface.geom.computeFaceNormals();
                    topface.geom.computeVertexNormals();

                    var edges = topface.geom.getEdges();

                    var linematerial = this.materialManager.getLinematerial();

                    let elementobject = new THREE.Object3D();
                    elementobject.name = SceneLayerName.Entity;

                    let elementlabel = new THREE.Object3D();
                    elementlabel.name = SceneLayerName.Label;

                    elementobject.add(elementlabel);

                    edges.forEach(function (edge: any) {
                        var lineseggeometry = new LineSegmentsGeometry();
                        lineseggeometry.setPositions([
                            edge.start.x,
                            edge.start.y,
                            edge.start.z,
                            edge.end.x,
                            edge.end.y,
                            edge.end.z,
                        ]);
                        var line = new THREE.Line(lineseggeometry, linematerial);
                        line.translateZ(entity.hsbmodel.Units.convert(180));
                        elementobject.add(line);
                        line.userData.originalmaterial = linematerial;
                    });

                    var existingbody = entitieslayer.getObjectById(entity.geometryModel.id);

                    if (existingbody) {
                        entitieslayer.remove(existingbody);
                    }

                    var body = new THREE.Mesh(topface.geom, material);
                    body.translateZ(entity.hsbmodel.Units.convert(180));

                    this.ApplyMaterial(entity, material);
                    elementobject.add(body);
                    body.userData.originalmaterial = material;
                    body.userData.handle = entity.handle;

                    body.geometry.computeBoundingBox();
                    center = body.geometry.boundingBox.getCenter(new THREE.Vector3());

                    var textsize = entity.hsbmodel.Units.convert(300);
                    var textheight = entity.hsbmodel.Units.convert(60);
                    let txtstring = entity.objecttype;

                    var filterednode = project.nodeModel.filteredNodes.filter(function (e: any) {
                        return e.entity === entity;
                    })[0];

                    if (filterednode) {
                        txtstring = filterednode.name;
                    }

                    var txtpt = center
                        .clone()
                        .add(
                            new THREE.Vector3(0, 0, 1).multiplyScalar(
                                entity.hsbmodel.Units.convert(200)
                            )
                        );
                    material = this.materialManager.getRedmaterial();

                    var text = this.CreateTextObject(
                        txtstring,
                        textsize,
                        textheight,
                        body.matrix,
                        material,
                        'centre',
                        txtpt
                    );

                    text.name = SceneLayerName.Label;
                    text.userData.modelId = entity.hsbmodel.modelId;
                    text.userData.handle = entity.handle;
                    text.userData.originalmaterial = material;

                    elementlabel.add(text);

                    entity.geometryModel = elementobject;
                    elementobject.userData.modelId = entity.hsbmodel.modelId;
                    elementobject.userData.handle = entity.handle;
                    entity.selectmaterial = this.materialManager.getSelectMaterial();
                    entity.highlightmaterial = this.materialManager.getHighlightMaterial();
                    entity.hidematerial = this.materialManager.getHidematerial();
                    entity.ghostmaterial = this.materialManager.getGhostmaterial();

                    entitieslayer.add(elementobject);
                }
            }
        }
    }
    /**
     * Creates grid lines for a grid object
     * @param entity
     * @param gridObject
     * @param gridLineArr
     * @param isXdir
     */
    private addGridLines(
        entity: any,
        gridObject: THREE.Object3D,
        gridLineArr: any[],
        isXdir: boolean
    ) {
        let self = this;
        let linematerial = this.materialManager.getLinematerial();
        let coordSys = entity.coordSys.clone();
        gridLineArr.forEach(function (gridLine: any) {
            var lineseggeometry = new LineSegmentsGeometry();

            let vecOffset = isXdir ? coordSys.vecX : coordSys.vecY;
            let vecLength = isXdir ? coordSys.vecY : coordSys.vecX;
            let length = isXdir ? entity.sizey : entity.sizex;

            var ptStart = coordSys.ptOrg
                .clone()
                .add(vecOffset.clone().multiplyScalar(gridLine.offset));
            var ptEnd = ptStart.clone().add(vecLength.clone().multiplyScalar(length));
            var ptCenter = ptStart
                .clone()
                .add(vecLength.clone().multiplyScalar(length / 2))
                .add(vecOffset.clone().multiplyScalar(10000 / 2));

            lineseggeometry.setFromPoints([ptStart, ptEnd]);
            var line = new THREE.Line(lineseggeometry, linematerial);
            line.userData.originalmaterial = linematerial;
            gridObject.add(line);

            var planegeom = new THREE.PlaneGeometry(
                entity.hsbmodel.Units.convert(length),
                entity.hsbmodel.Units.convert(10000)
            );
            var hideMat = self.materialManager.getGhostmaterial();
            var docobject = new THREE.Mesh(planegeom, hideMat);

            var coordsys = new CoordSys();
            coordsys.ptOrg = ptCenter;
            coordsys.vecX = vecLength;
            coordsys.vecY = coordSys.vecZ;
            coordsys.vecZ = vecOffset;

            var matrix4 = new THREE.Matrix4();
            GeometryHelpers.setFromCoordSys(matrix4, coordsys);

            docobject.applyMatrix4(matrix4);
            docobject.updateMatrixWorld(true);

            var textsize = entity.hsbmodel.Units.convert(200);
            var textheight = entity.hsbmodel.Units.convert(50);

            if (gridLine.coordinate) {
                var ptStartText = ptStart
                    .clone()
                    .add(
                        vecLength
                            .clone()
                            .multiplyScalar(-textsize * 1.25 * gridLine.coordinate.length)
                    );
                var ptStartEnd = ptEnd
                    .clone()
                    .add(
                        vecLength
                            .clone()
                            .multiplyScalar(textsize * 1.25 * gridLine.coordinate.length)
                    );

                var textStart = self.CreateTextObject(
                    gridLine.coordinate,
                    textsize,
                    textheight,
                    new THREE.Matrix4(),
                    linematerial,
                    'centre',
                    ptStartText
                );
                textStart.userData.originalmaterial = linematerial;
                textStart.updateMatrix();
                gridObject.add(textStart);

                var textEnd = self.CreateTextObject(
                    gridLine.coordinate,
                    textsize,
                    textheight,
                    new THREE.Matrix4(),
                    linematerial,
                    'centre',
                    ptStartEnd
                );
                textEnd.userData.originalmaterial = linematerial;
                textEnd.updateMatrix();
                gridObject.add(textEnd);
            }
        });
    }

    /**
     * Creates a arrowhead mesh
     * @param from Starting point for the arrowhead
     * @param to End point for the arrow head.
     * @param arrowLength optional length of the arrow head.
     */
    public CreateArrow(from: THREE.Vector3, to: THREE.Vector3, arrowLength?: number) {
        let dir = to.clone().sub(from).normalize();
        let length = arrowLength || from.distanceTo(to);
        let fromPoint = to.clone().sub(dir.clone().multiplyScalar(length * 0.5));
        let coneGeom = new THREE.CylinderGeometry(0, 0.5, 1, 16, 1);
        let arrow = new THREE.Mesh(coneGeom, new THREE.MeshBasicMaterial({ color: 0x000000 }));
        arrow.position.copy(fromPoint);
        let axis = new THREE.Vector3();
        arrow.matrixAutoUpdate = false;
        axis.set(dir.z, 0, -dir.x).normalize();
        const radians = Math.acos(dir.y);
        arrow.quaternion.setFromAxisAngle(axis, radians);
        arrow.scale.set(length * 0.8, length, length * 0.8);
        arrow.updateMatrix();
        arrow.name = 'Arrow';
        return arrow;
    }

    /**
     * Creates a text object in world space.
     * @param label The desired text to be displayed
     * @param size The font height
     * @param height
     * @param matrix4
     * @param material
     * @param justification
     * @param position
     */
    public CreateTextObject(
        label: string,
        size: number,
        height: number,
        matrix4: THREE.Matrix4,
        material: THREE.Material,
        justification: string,
        position: THREE.Vector3
    ): THREE.Mesh {
        var text3d = new TextGeometry(label.toString(), {
            size: size,
            height: height,
            curveSegments: 1,
            font: this._optimerBold,
        });
        let text = new THREE.Mesh(text3d, material);
        text.applyMatrix4(matrix4);
        let aabb = new THREE.Box3().setFromObject(text);
        var centerOffset = aabb.max.x - aabb.min.x;
        text.position.x = position.x;
        text.position.y = position.y;
        text.position.z = position.z;

        if (justification === 'centre') {
            centerOffset *= -0.5;
        } else if (justification === 'right') {
            centerOffset *= -1.0;
        } else {
            centerOffset = 0.0;
        }
        text.translateX(centerOffset);
        return text;
    }

    /**
     * Creates a rect with rounded corners
     * @param ctx context
     * @param x start X
     * @param y start Y
     * @param w width
     * @param h height
     * @param r radius of corners
     */
    public RoundRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        w: number,
        h: number,
        r: number
    ) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    }

    /**
     * Creates a dashed line from the given point to the reference point
     * @param point The point form where to start
     * @param ref The reference point
     */
    public CreateRefLine(point: THREE.Vector3, ref: any) {
        let refPoint: THREE.Vector3[] = [];
        refPoint.push(point);
        refPoint.push(ref.pt);
        let refGeom = new THREE.BufferGeometry().setFromPoints(refPoint);
        let refObject = new THREE.Line(refGeom, this.materialManager.getDottedLinematerial());
        // Line needs to compute distances before it can apply dash material
        refObject.computeLineDistances();
        refObject.name = 'Reference';
        return refObject;
    }

    /**
     * Creates the necessary symbols for the given visuals
     * @param scene The scene that these visuals belong to.
     */
    createHSBEntityLabels(scene: ViewerScene, project: HsbProject) {
        let self = this;

        let entities: any = [];
        let test = Object.values(project.EntityTable);
        test.forEach((value: any) => {
            entities = entities.concat(Object.values(value));
        }, []);
        entities = entities.filter((e: any) => !!e);

        if (entities.length === 0) {
            self.loadingProgress.value += LoadingSteps.labelCreation;
        } else
            entities.forEach((entity: HsbEntity, index: any, entities: any) => {
                if (
                    entity instanceof Sip ||
                    entity instanceof ElementRoofSIP ||
                    entity instanceof ElementRoofSIP
                ) {
                    self.createSipGrainDirectionIcon(entity);
                } else if (entity instanceof HsbElement) {
                    self.createElementLabel(entity, scene);
                } else if (entity.objecttype === ObjectType.IfcSpace) {
                    self.addSpaceLabel(entity, scene, project);
                }
            });
        self.loadingProgress.value += LoadingSteps.labelCreation;
    }

    /**
     * Adds outline labels to faces
     * @param entitieslayer
     * @param visual
     * @returns
     */
    addSpaceLabel(entity: HsbEntity, scene: ViewerScene, project: HsbProject) {
        // Early out
        if (
            entity.objecttype !== ObjectType.IfcSpace ||
            !entity.searchnode ||
            entity.searchnode.objecttype !== entity.objecttype ||
            !entity.object
        )
            return;

        let object = entity.object as THREE.Mesh | THREE.InstancedMesh;
        let instanceId = -1;
        if (object instanceof THREE.InstancedMesh) {
            instanceId = object.userData.handle.findIndex((h: string) => h === entity.handle);
        }
        if (!object) return;

        let entitieslayer = scene.GetLayer(SceneLayerName.Entities);
        if (!entitieslayer) return;

        var faces = this.getAllignedFaces(object);
        if (!faces || faces.length === 0) return;

        // Getting matrices
        object.geometry.computeBoundingBox();
        var bBox = object.geometry.boundingBox;
        let mat4: THREE.Matrix4;
        if (instanceId > -1) {
            mat4 = new THREE.Matrix4();
            (object as THREE.InstancedMesh).getMatrixAt(instanceId, mat4);
        }
        if (bBox) bBox.applyMatrix4(instanceId > -1 ? mat4! : object.matrixWorld);

        faces.sort(function (a, b) {
            return a.center.z - b.center.z;
        });
        let bottomFace = faces[0];
        var material = this.materialManager.getGreenmaterial();
        // Element object
        let root = new THREE.Object3D();
        root.name = SceneLayerName.Entity;
        let elementlabel = new THREE.Object3D();
        elementlabel.name = SceneLayerName.Label;
        root.add(elementlabel);
        // Body
        let geom = new THREE.BufferGeometry().setFromPoints(bottomFace.vertices);
        var existingbody = entitieslayer.getObjectById(object.id);
        if (existingbody) {
            entitieslayer.remove(existingbody);
        }
        var body = new THREE.Mesh(geom, material);
        body.userData.originalmaterial = material;
        body.userData.handle = entity.handle;
        body.userData.modelId = entity.hsbmodel.modelId;
        body.translateZ(entity.hsbmodel.Units.convert(180));
        root.add(body);
        //Edges
        var edges = new THREE.EdgesGeometry(geom);
        let linematerial = this.materialManager.getLinematerial();
        let linesBody = new THREE.LineSegments(edges, linematerial);
        linesBody.translateZ(entity.hsbmodel.Units.convert(180));
        root.add(linesBody);
        // Text
        let txtstring = entity.objecttype;
        var filterednode = project.nodeModel.filteredNodes.filter(function (e: any) {
            return e.entity === entity;
        })[0];
        if (filterednode) {
            txtstring = filterednode!.name!;
        }
        var text = this.createTextSpriteObject(txtstring, {
            fontsize: entity.hsbmodel.Units.convert(100),
        });
        if (!text) return;
        text.name = SceneLayerName.Label;
        text.layers.set(ViewerLayers.LABELS);
        text.userData.handle = entity.handle;
        text.userData.modelId = entity.hsbmodel.modelId;
        elementlabel.add(text);

        object.add(root);
        root.userData.handle = entity.handle;
        root.userData.modelId = entity.hsbmodel.modelId;
    }

    getAllignedFaces(mesh: THREE.Mesh): {
        indices: number[];
        normal: THREE.Vector3;
        vertices: THREE.Vector3[];
        center: THREE.Vector3;
    }[] {
        let geometry = mesh.geometry as THREE.BufferGeometry;
        let normals = geometry.getAttribute('normal');
        let positions = geometry.getAttribute('position');

        // First we create all 3-gons
        let facesQty = positions.count / 3;
        let unresolvedFaces: {
            a: THREE.Vector3 | null;
            b: THREE.Vector3 | null;
            c: THREE.Vector3 | null;
            normal: THREE.Vector3 | null;
            index: number;
        }[] = [];
        for (let faceIndex = 0; faceIndex < facesQty; faceIndex++) {
            let face: {
                a: THREE.Vector3 | null;
                b: THREE.Vector3 | null;
                c: THREE.Vector3 | null;
                normal: THREE.Vector3 | null;
                index: number;
            } = { a: null, b: null, c: null, normal: null, index: faceIndex };
            face.a = new THREE.Vector3().fromBufferAttribute(positions, faceIndex * 3);
            face.b = new THREE.Vector3().fromBufferAttribute(positions, faceIndex * 3 + 1);
            face.c = new THREE.Vector3().fromBufferAttribute(positions, faceIndex * 3 + 2);

            let a = new THREE.Vector3().fromBufferAttribute(normals, faceIndex * 3);
            let b = new THREE.Vector3().fromBufferAttribute(normals, faceIndex * 3 + 1);
            let c = new THREE.Vector3().fromBufferAttribute(normals, faceIndex * 3 + 2);
            face.normal = a.clone().add(b).add(c).divideScalar(3).normalize();
            unresolvedFaces.push(face);
        }

        // Then we iterate over those and check what faces allign.
        let resolvedFaces: any[] = [];
        let currentFace: {
            indices: number[];
            normal: THREE.Vector3 | null;
            vertices: THREE.Vector3[];
            center: THREE.Vector3 | null;
        } = { indices: [], normal: null, vertices: [], center: null };
        unresolvedFaces.forEach((face, index) => {
            if (!currentFace.normal) {
                // First item
                currentFace.normal = face.normal;
                currentFace.vertices = [face.a!, face.b!, face.c!];
                currentFace.indices = [index];
            } else if (currentFace.normal.dot(face.normal!) > 0.999) {
                // Same normal
                currentFace.indices.push(index);
                currentFace.vertices.push(face.a!, face.b!, face.c!);
            } else {
                // Not same normal
                // Filter duplicate vertices
                currentFace.vertices.filter(
                    (value: THREE.Vector3, index, array: THREE.Vector3[]) => {
                        return (
                            array.findIndex((vector) => {
                                return (
                                    Math.abs(vector.x - value.x) < Number.EPSILON &&
                                    Math.abs(vector.y - value.y) < Number.EPSILON &&
                                    Math.abs(vector.z - value.z) < Number.EPSILON
                                );
                            }) === index
                        );
                    }
                );
                // Get center
                currentFace.center = currentFace.vertices
                    .reduce((a, b) => a.add(b))
                    .divideScalar(currentFace.vertices.length);
                resolvedFaces.push(currentFace!);
                currentFace.normal = face.normal;
                currentFace.vertices = [face.a!, face.b!, face.c!];
                currentFace.indices = [index];
            }
        });

        // Special case if first and last face align
        if (resolvedFaces.length > 1) {
            let first = resolvedFaces[0];
            let last = resolvedFaces[resolvedFaces.length - 1];
            if (first.normal.dot(last.normal) > 0.999) {
                first.vertices = first.vertices.concat(last.vertices);
                first.indices = first.indices.concat(last.indices);
                resolvedFaces.pop();
                first.center = first.vertices
                    .reduce((a: { add: (arg0: any) => any }, b: any) => a.add(b))
                    .divideScalar(first.vertices.length);
            }
        }

        return resolvedFaces;
    }

    /**
     * Creates the symbol to indicate the grain direction of a SIP
     * @param visualLayer The visual layer that is currently in use
     * @param entitieslayer The entitieslayer for this scene
     * @param visual The visual for which to generate a symbol
     */
    createSipGrainDirectionIconOLD(
        visualLayer: HsbVisualLayer,
        entitieslayer: THREE.Object3D,
        visual: HsbVisual
    ) {
        let entity: HsbEntity = visual?.entity;
        if (!entity) return;
        // Early out
        if (
            !(entity instanceof Sip) ||
            !entity.woodgraindirection ||
            entity.initialCoordSys?.ptOrg.equals(new THREE.Vector3(0, 0, 0))
        )
            return;

        // Getting matrices
        let entityCoordSys = entity.initialCoordSys;

        if (entity.initialCoordSys.ptOrg.equals(new THREE.Vector3(0, 0, 0))) {
            entityCoordSys = entity.coordSys.clone();
        }

        var m4ToWorld = new THREE.Matrix4();
        var m4ToLocal = new THREE.Matrix4();
        m4ToWorld.makeBasis(entityCoordSys.vecX, entityCoordSys.vecY, entityCoordSys.vecZ);
        m4ToWorld.setPosition(entityCoordSys.ptOrg);
        m4ToLocal.copy(m4ToWorld).invert();

        // Creating/Getting visual and object
        let existingVisual: any = entity && visualLayer && visualLayer?.getVisuals([entity])?.[0];
        if (existingVisual) {
            var labelvisual: HsbVisual = existingVisual;
            var elementObject: THREE.Object3D = labelvisual.geometryModel;
        } else {
            labelvisual = new HsbVisual();
            visualLayer.addVisual(entity.hsbmodel.modelId, labelvisual);
            elementObject = new THREE.Object3D();
        }

        labelvisual.selectmaterial = this.materialManager.getSelectMaterial();
        labelvisual.highlightmaterial = this.materialManager.getHighlightMaterial();
        labelvisual.hidematerial = this.materialManager.getGhostmaterial();
        labelvisual.entity = entity;

        elementObject.name = SceneLayerName.Entity;

        var shape = new THREE.Shape();
        shape.moveTo(entity.hsbmodel.Units.convert(197), entity.hsbmodel.Units.convert(15));
        shape.lineTo(entity.hsbmodel.Units.convert(150), entity.hsbmodel.Units.convert(55));
        shape.lineTo(entity.hsbmodel.Units.convert(300), entity.hsbmodel.Units.convert(-15));
        shape.lineTo(entity.hsbmodel.Units.convert(-197), entity.hsbmodel.Units.convert(-15));
        shape.lineTo(entity.hsbmodel.Units.convert(-150), entity.hsbmodel.Units.convert(-55));
        shape.lineTo(entity.hsbmodel.Units.convert(-300), entity.hsbmodel.Units.convert(15));
        shape.lineTo(entity.hsbmodel.Units.convert(197), entity.hsbmodel.Units.convert(15));

        // Arrow geometry
        /**
         * @todo: extrude or flat
         */
        //var geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        var geometry = new THREE.ShapeGeometry(shape);

        // Arrow mesh
        var arrow = new THREE.Mesh(geometry, this.materialManager.getRedmaterial());
        arrow.userData.originalmaterial = this.materialManager.getRedmaterial();
        arrow.name = SceneLayerName.Arrow;
        arrow.layers.set(ViewerLayers.LABELS);
        arrow.applyMatrix4(GeometryHelpers.SwitchHandnessMatrix);

        // Apply graindirection
        var grainDirection = entity.woodgraindirection.clone();
        var angle = entityCoordSys.vecX.angleTo(grainDirection);
        arrow.translateOnAxis(new THREE.Vector3(0, 0, 1).applyMatrix4(arrow.matrixWorld), -100);
        arrow.rotateZ(angle);
        // If flat:
        arrow.rotateX(Math.PI);

        // Element label
        let elementlabel = new THREE.Object3D();
        elementlabel.name = SceneLayerName.Label;
        elementlabel.add(arrow);

        arrow.updateMatrix();

        elementObject.add(elementlabel);
        entitieslayer.add(elementObject);
        // Setting coordsys
        var coordsys = new CoordSys();
        elementlabel.matrix.extractBasis(coordsys.vecX, coordsys.vecY, coordsys.vecZ);
        coordsys.ptOrg.setFromMatrixPosition(elementlabel.matrix);
        entity.labelCoordSys = coordsys;
    }

    getCenterPoint(mesh: { geometry: any; localToWorld: (arg0: THREE.Vector3) => void }) {
        var middle = new THREE.Vector3();
        var geometry = mesh.geometry;

        geometry.computeBoundingBox();

        middle.x = (geometry.boundingBox.max.x + geometry.boundingBox.min.x) / 2;
        middle.y = (geometry.boundingBox.max.y + geometry.boundingBox.min.y) / 2;
        middle.z = (geometry.boundingBox.max.z + geometry.boundingBox.min.z) / 2;

        mesh.localToWorld(middle);
        return middle;
    }

    createSipGrainDirectionIcon(entity: HsbEntity) {
        if (!entity) return;
        // Early out
        if (!(entity instanceof Sip) || !entity.woodgraindirection) return;

        let object = entity.object as THREE.Mesh | THREE.InstancedMesh;
        if (!object) return;
        let width = entity.getproperty('solidheight')?.value ?? entity.getproperty('dh')?.value;

        // Apply graindirection
        let grainDirection: THREE.Vector3 = entity.woodgraindirection.clone();
        grainDirection.applyMatrix4(GeometryHelpers.SwitchHandnessMatrix);
        let { ptOrg, vecX, vecY, vecZ } = entity.coordSys.clone();

        // Arrow shape
        let shape = new THREE.Shape();
        let shapeDepth = width * 1.1;
        shape.moveTo(entity.hsbmodel.Units.convert(197), entity.hsbmodel.Units.convert(15));
        shape.lineTo(entity.hsbmodel.Units.convert(150), entity.hsbmodel.Units.convert(55));
        shape.lineTo(entity.hsbmodel.Units.convert(300), entity.hsbmodel.Units.convert(-15));
        shape.lineTo(entity.hsbmodel.Units.convert(-197), entity.hsbmodel.Units.convert(-15));
        shape.lineTo(entity.hsbmodel.Units.convert(-150), entity.hsbmodel.Units.convert(-55));
        shape.lineTo(entity.hsbmodel.Units.convert(-300), entity.hsbmodel.Units.convert(15));
        shape.lineTo(entity.hsbmodel.Units.convert(197), entity.hsbmodel.Units.convert(15));
        let extrudeSettings = {
            steps: 2,
            depth: shapeDepth,
            bevelEnabled: true,
            bevelThickness: entity.hsbmodel.Units.convert(5),
            bevelSize: entity.hsbmodel.Units.convert(3),
            bevelSegments: 3,
        };
        let geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        // ROtate geom
        geometry.rotateY(Math.PI / 2);
        let angleY = vecY.angleTo(grainDirection);
        geometry.rotateZ(angleY);
        let angleX = vecX.angleTo(grainDirection);
        geometry.rotateZ(angleX - Math.PI / 2);

        // Arrow object
        let arrow = new THREE.Mesh(geometry, this.materialManager.getRedmaterial());
        arrow.name = SceneLayerName.Arrow + '-' + entity.handle;
        arrow.layers.set(ViewerLayers.LABELS);
        // Get matrix

        let pos = ptOrg.clone();
        // Left handed => Right handed
        pos.applyMatrix4(GeometryHelpers.SwitchHandnessMatrix);

        // Get transform
        let m4 = new THREE.Matrix4();
        m4.makeBasis(vecX, vecY, vecZ);
        m4.setPosition(pos.x, pos.y, pos.z);
        arrow.applyMatrix4(m4);
        // Attach to scene
        object.attach(arrow);
        // Grain direction
        arrow.lookAt(pos.clone().add(grainDirection));

        if (object instanceof THREE.InstancedMesh) {
            arrow.applyMatrix4(object.matrixWorld);
        }
        // Offset
        let offset = new THREE.Vector3(0, 0, -shapeDepth / 2).applyQuaternion(arrow.quaternion);
        arrow.position.add(offset);
        // Label
        let text = this.createSIPLabel(entity);
        if (text) {
            text.translateZ(200);
            arrow.add(text);
            entity.labelCoordSys = GeometryHelpers.getCoordSysMatrix(arrow.matrix.clone());
        }
        arrow.updateMatrixWorld();
    }

    /**
     * Creates a label for each element.
     * @param visualLayer The visual layer to which the visual belongs
     * @param entitieslayer The entitylayer ot which the visual belongs
     * @param visual The visual for which to create a element label
     */
    createElementLabel(entity: HsbElement, scene: ViewerScene) {
        // Early out
        try {
            if (!(entity instanceof HsbElement)) return;
            // IF this element holds SIP/CLT entities it should not get a label
            if (
                entity.entities?.findIndex(
                    (e: any) =>
                        e instanceof Sip ||
                        e instanceof ElementRoofSIP ||
                        e instanceof ElementRoofSIP
                ) > -1
            )
                return;
            // Getting positions
            let { ptOrg } = entity.elementCoordSys ?? entity.coordSys;
            let actualPos = new THREE.Vector3(ptOrg.x, ptOrg.z, -ptOrg.y);

            // Root Object
            let root: THREE.Group;
            if (!entity.object) {
                root = new THREE.Group();
                root.userData.handle = entity.handle;
                root.userData.modelId = entity.hsbmodel.modelId;
                root.name = SceneLayerName.Entity;
                root.layers.set(ViewerLayers.LABELS);
                root.position.copy(actualPos);
            } else root = entity.object as THREE.Group;

            // root.applyMatrix4(m4toWorld);
            root.updateMatrixWorld();
            // Element label
            let elementlabel = new THREE.Object3D();
            elementlabel.name = SceneLayerName.Label;
            elementlabel.layers.set(ViewerLayers.LABELS);
            // offset
            if (entity.ptarrow) {
                let arrowPos = new THREE.Vector3(
                    entity.ptarrow.x,
                    entity.ptarrow.z,
                    -entity.ptarrow.y
                );
                // Crossing gives us the depth direction
                let outDir = arrowPos
                    .clone()
                    .sub(actualPos)
                    .normalize()
                    .cross(new THREE.Vector3(0, 1, 0));
                // Direction label should be in
                let { vecZ, vecX } = entity.elementCoordSys ?? entity.coordSys;
                let dir = vecZ.clone().add(vecX).cross(new THREE.Vector3(0, 1, 0));
                let offset = outDir.multiplyScalar(500 * Math.sign(dir.z));
                elementlabel.position.copy(arrowPos.clone().add(offset));
            }

            // Text label
            var message: string =
                (entity.getproperty('code')?.value || '') +
                (entity.getproperty('number')?.value || '');
            if (!message || message === '') return;
            var text = this.createTextSpriteObject(message, { fontsize: 120 });
            if (!text) return;
            text.layers.set(ViewerLayers.LABELS);
            text.userData.handle = entity.handle;
            text.userData.modelId = entity.hsbmodel.modelId;
            text.userData.visible = true;
            elementlabel.add(text);
            //Final setup
            // .attach() because we gave the label a pos in world space, not in local space.
            root.attach(elementlabel);
            if (!entity.object) {
                scene.GetLayer(SceneLayerName.Entities).add(root);
                entity.object = root;
            }
            // Passing coordsys data
            var coordsys = new CoordSys();
            elementlabel.matrix.extractBasis(coordsys.vecX, coordsys.vecY, coordsys.vecZ);
            coordsys.ptOrg.setFromMatrixPosition(elementlabel.matrix);
            entity.labelCoordSys = entity.elementCoordSys;
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Creates a label for each sips or clt entity.
     * @param visual The visual for which to create a element label
     */
    createSIPLabel(entity: HsbEntity) {
        // let object = entity.object as THREE.Mesh | THREE.InstancedMesh;
        // Early out
        if (!(entity instanceof Sip)) return;

        // Text label
        let label = entity.getproperty('label')?.value;
        let sublabel = entity.getproperty('sublabel')?.value;
        var message: string = (label || '') + (sublabel || '');
        message = message.trim().replace(/\s/g, '');

        if (!message || message === '') return;
        var text = this.createTextSpriteObject(message, {
            fontsize: entity.hsbmodel.Units.convert(100),
        });
        if (!text) return null;
        text.layers.set(ViewerLayers.LABELS);
        text.userData.handle = entity.handle;
        text.userData.modelId = entity.hsbmodel.modelId;
        return text;
    }

    /**
     * Creates a sprite object containing a label
     * @param message The message to be displayed on the label
     * @param parameters Parameters for the desired label
     * @returns The created new sprite.
     */
    createTextSpriteObject(
        message: string,
        parameters?: {
            font?: string;
            fontsize?: number;
            canvassize?: number;
            borderThickness?: number;
            textcolor?: number;
            borderColor?: number;
            backgroundColor?: number;
            padding?: number;
        }
    ) {
        let params = parameters || {};
        let fontface = params.font || 'Monospace';
        let fontsize = params.fontsize || 100;
        let canvas = document.createElement('canvas');
        let adjustText = !params.fontsize && params.canvassize;
        let adjustCanvas = !params.canvassize && params.fontsize;
        let padding = params.padding || 50;

        canvas.width = params.canvassize ? params.canvassize : 100;
        canvas.height = params.canvassize ? params.canvassize * 0.5 : 50;
        let borderThickness = params.borderThickness || canvas.width / 40;
        let context = canvas.getContext('2d');
        if (!context) return null;
        context.fillStyle = 'white';
        context.font = fontsize + 'px ' + fontface;

        if (adjustText) {
            do {
                fontsize--;
                context.font = fontsize + 'px ' + fontface;
            } while (context.measureText(message).width + padding > canvas.width);
        } else if (adjustCanvas) {
            let measurement = context.measureText(message);
            canvas.width = measurement.width + padding;
            canvas.height =
                (measurement.actualBoundingBoxAscent + measurement.actualBoundingBoxDescent) * 2 +
                padding;
        }
        // Apply colors

        context.fillStyle = params.backgroundColor
            ? '#' + params.backgroundColor?.toString(16)
            : 'rgba(255, 255, 255, 1.0)';
        context.strokeStyle = params.borderColor
            ? '#' + params.borderColor.toString(16)
            : 'rgba(20, 20, 20, 1.0)';
        context.lineWidth = borderThickness;
        this.RoundRect(
            context,
            borderThickness / 2,
            borderThickness / 2,
            canvas.width - borderThickness * 2,
            fontsize * 1.4 - borderThickness * 2,
            canvas.width / 10
        );
        // text color
        context.font = fontsize + 'px ' + fontface;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = params.textcolor ? '#' + params.textcolor?.toString(16) : '#000000';
        context.fillText(message, canvas.width / 2, canvas.height / 2);

        // canvas contents will be used for a texture
        let texture = new THREE.Texture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.needsUpdate = true;
        let spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            sizeAttenuation: true,
        });
        let sprite = new THREE.Sprite(spriteMaterial);
        sprite.userData.originalmaterial = spriteMaterial;
        sprite.scale.set(canvas.width, canvas.height, 1);
        sprite.updateMatrix();

        return sprite;
    }
}
